import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { LockKeyhole, Eye, EyeOff } from "lucide-react";
import {
  useValidateResetPasswordLink,
  useResetPassword,
  type ResetPasswordForm,
  type ApiError,
} from "@iziping/common";
import { useNotification } from "../Notification/useNotification";
import InputIcon from "../InputIcon/InputIcon";
import styles from "./Auth.module.css";

interface Props {
  logo?: string;
  title?: string;
  subtitle?: string;
  cardStyle?: React.CSSProperties;
}

export const ResetPasswordPage = ({
  logo,
  title,
  subtitle,
  cardStyle,
}: Props) => {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    isLoading,
    isError,
    error: linkError,
  } = useValidateResetPasswordLink(token);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    defaultValues: { password: "", passwordConfirmation: "" },
  });

  const password = watch("password");
  const resetPassword = useResetPassword();

  useEffect(() => {
    if (isError) {
      notify(t(linkError.error), "error");
      navigate("/notFound");
    }
  }, [isError, linkError, notify, navigate, t]);

  const onSubmit = async (form: ResetPasswordForm) => {
    try {
      await resetPassword.mutateAsync({ data: { ...form, token: token! } });
      notify(t("login.resetPassword.success"), "success");
      navigate("/login");
    } catch (err) {
      const apiError = err as ApiError;
      notify(t(apiError?.error) || t("error.unknown"), "error");
    }
  };

  if (isLoading) return <div>{t("common.loading")}</div>;

  return (
    <div className={styles.page}>
      <form
        className={styles.card}
        style={cardStyle}
        onSubmit={handleSubmit(onSubmit)}
      >
        {title ? (
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        ) : (
          logo && (
            <div className={styles.logoContainer}>
              <img src={logo} alt="Logo" className={styles.logo} />
            </div>
          )
        )}

        <div className={styles.formGroup}>
          <label htmlFor="password">{t("login.resetPassword.password")}</label>
          <InputIcon
            icon={LockKeyhole}
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("login.resetPassword.password")}
            rightIcon={showPassword ? EyeOff : Eye}
            onRightIconClick={() => setShowPassword((v) => !v)}
            {...register("password", {
              required: t("login.resetPassword.password.required"),
              minLength: {
                value: 8,
                message: t("login.resetPassword.password.minLength"),
              },
              pattern: {
                value: /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                message: t("login.resetPassword.password.pattern"),
              },
            })}
          />
          {errors.password && (
            <span className={styles.error}>{errors.password.message}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="passwordConfirmation">
            {t("login.resetPassword.passwordConfirmation")}
          </label>
          <InputIcon
            icon={LockKeyhole}
            id="passwordConfirmation"
            type={showConfirmation ? "text" : "password"}
            placeholder={t("login.resetPassword.passwordConfirmation")}
            rightIcon={showConfirmation ? EyeOff : Eye}
            onRightIconClick={() => setShowConfirmation((v) => !v)}
            {...register("passwordConfirmation", {
              required: t("login.resetPassword.passwordConfirmation.required"),
              validate: (value) =>
                value === password ||
                t("login.resetPassword.passwordConfirmation.match"),
            })}
          />
          {errors.passwordConfirmation && (
            <span className={styles.error}>
              {errors.passwordConfirmation.message}
            </span>
          )}
        </div>

        <button type="submit" className={styles.button}>
          {t("login.resetPassword.confirm.button")}
        </button>
      </form>
    </div>
  );
};
